/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used
 * in more than one place (or representing a product decision, not a local
 * detail) lives here, not as a loose `const` inside a component.
 *
 * Reason: these values had been redeclared in ~12 files using two spellings
 * of the same number (`5 * 60 * 1000` and `300_000`), so changing
 * the cache policy required touching all those locations and risked
 * leaving divergent values. Import from here instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/** Milliseconds in a minute. Base for deriving the remaining durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Minutes a cached datum is considered fresh (offline and session cache). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally via PowerSync, so a generous `staleTime`
 * (5 min) avoids redundant refetches/re-renders without any real risk of
 * showing stale data. It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It remains a separate constant (although it currently matches the offline
 * cache value) because it is an independent decision: it must stay aligned
 * across `useSession`, the `_app` route guard, and the `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Ceiling for waiting on a `get-session` made INSIDE a route `beforeLoad`.
 *
 * P23/CC-60 · I1-bis: a `beforeLoad` that awaits the network without a ceiling does not fail
 * with a 401 or 503 (both resolve quickly) — it fails when the response
 * is SLOWED: the router does not mount the route, the `<Suspense fallback={null}>`
 * in `__root` has nothing to render, and the entire app stays BLANK whether
 * the network is fast or slow. Measured here on `main`: on `/login`,
 * `body.innerText` = 11 chars (the static `#app-loader` in `index.html`) at
 * 3 s and **0 chars** at 6/10/15/20 s, with `#root` empty; on `/` the same
 * probe returned 4607 chars because the sibling `_app` guard DID have a ceiling.
 *
 * SINGLE source for the two route guards (`_app` and `_auth`): having it as a
 * local literal inside `_app` was exactly what left `_auth` without the
 * defense that `_app` had. After the ceiling, the screen renders and the
 * late response continues syncing the machine/cache — `BootManager`
 * redirects afterward if a session turned out to exist.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/**
 * Delay after a social-login error before redirecting to `/login`, so the
 * user can read the toast with the reason at `/auth/social/callback`.
 */
export const SOCIAL_ERROR_REDIRECT_DELAY_MS = 2500;
